import {isPayloadTooLarge} from '@/core/content-links';
import { useEffect,useRef,useState, type ReactNode, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@/app/contract';
import {useMe,useServerConfig} from '@/model';
import { Button, Banner, ErrorState, EmptyState, Spinner, NavigationBar, Icon, TextField, Select, TextArea } from '@/ui';
import { isConflict, isLost } from './data/resources';
import type { Kind, ItemRecord } from './data/types';
import type { useWrite } from './data/resources';
import './spaces.css';
export function useCopy() { const { t, i18n } = useTranslation('spaces'); return { t, locale: i18n.resolvedLanguage?.startsWith('zh') ? 'zh-Hans' as const : 'en' as const }; }
export function SourceAccountLabel(){
    const me=useMe(),config=useServerConfig();const realm=config?.realm;
    const realmName=typeof config?.realm_name==='string'?config.realm_name:realm&&typeof realm==='object'&&'name' in realm&&typeof realm.name==='string'?realm.name:undefined;
    return me?<p className="spaces-subtle spaces-source-account">{[me.account.display_name||me.account.handle,realmName].filter(Boolean).join(' · ')}</p>:null;
}
export function DraftLoading({ failed, onCancel }: {
    failed: boolean;
    onCancel: () => void;
}) { const { t } = useCopy(); return <div className="spaces-form">{failed ? <Banner tone="warning" title={t('ui.draftScopeUnavailable')}/> : <Spinner label={t('ui.loading')}/>}<Button variant="plain" onClick={onCancel}>{t('common.cancel')}</Button></div>; }
export const kindIcon: Record<string, string> = { task: 'checkmark.circle', workstream: 'square.stack.3d.up', board: 'rectangle.split.3x1', event: 'calendar', progress_entry: 'text.bubble', person: 'person', document: 'doc.text', project: 'folder', handoff: 'arrow.triangle.branch' };
export function KindIcon({ kind }: {
    kind: Kind;
}) { return <Icon name={kindIcon[kind] ?? 'questionmark.circle'} size={20}/>; }
export function useKindName() { const { t } = useCopy(); return (kind: string) => t(({ task: 'vocabulary.task', workstream: 'detail.workstream', board: 'detail.board', event: 'detail.event', progress_entry: 'detail.progressEntry', person: 'vocabulary.person', document: 'vocabulary.document', project: 'common.project', handoff: 'detail.handoff' } as Record<string, string>)[kind] ?? 'web.unsupported'); }
export function useStateName() { const { t } = useCopy(); return (state: string) => t(({ open: 'filters.open', active: 'detail.inProgress', waiting: 'detail.waiting', shelved: 'detail.shelved', done: 'common.done', completed: 'detail.completed', scheduled: 'detail.itemScheduled', cancelled: 'detail.cancelled', missed: 'detail.missed', pending: 'vocabulary.pending', accepted: 'detail.accepted', declined: 'detail.declined', acknowledged: 'detail.acknowledged', dropped: 'detail.dropped', sent: 'detail.sent', expired: 'detail.expired' } as Record<string, string>)[state] ?? 'vocabulary.statusUnavailable'); }
export function Page({ title, subtitle, children, actions, wide = false, back = false, className = '' }: {
    title: string;
    subtitle?: string;
    children: ReactNode;
    actions?: ReactNode;
    wide?: boolean;
    back?: boolean;
    className?: string;
}) {
    const nav = useNavigation();
    const { t } = useCopy();
    return <div className={`spaces-page ${className}`}><NavigationBar title={title} subtitle={subtitle} onBack={back||nav.layout==='phone' ? nav.back : undefined} backLabel={t('common.back')} trailing={actions}/><div className={`spaces-content ${wide ? 'spaces-wide' : ''}`}>{children}</div></div>;
}
export function ResourceState({ query, empty, children }: {
    query: {
        isPending: boolean;
        isError: boolean;
        error: unknown;
        data: unknown;
        refetch: () => unknown;
    };
    empty?: boolean;
    children: ReactNode;
}) {
    const { t } = useCopy();
    if (query.isPending)
        return <div className="spaces-centered"><Spinner label={t('ui.loading')}/></div>;
    if (query.isError && (!query.data || isLost(query.error)))
        return <ErrorState icon="exclamationmark.triangle" title={t(isPayloadTooLarge(query.error)?'files.tooLargeToPreview':isLost(query.error) ? 'ui.unavailable' : 'ui.error')} action={<Button onClick={() => void query.refetch()}>{t('common.tryAgain')}</Button>}/>;
    if (empty)
        return <EmptyState icon="tray" title={t('ui.empty')}/>;
    return <>{query.isError && <Banner title={t('web.cached')} tone="warning" action={<Button onClick={() => void query.refetch()}>{t('common.refresh')}</Button>}/>} {children}</>;
}
export function WriteError({ writer }: {
    writer: Pick<ReturnType<typeof useWrite>, 'error' | 'uncertain' | 'checkStatus'>;
}) {
    const { t } = useCopy();
    if (!writer.error&&!writer.uncertain)
        return null;
    return <Banner tone="error" title={t(isPayloadTooLarge(writer.error)?'files.tooLargeToPreview':writer.uncertain ? 'web.outcomeUnknown' : isConflict(writer.error) ? 'ui.conflict' : writer.error instanceof Error && ['epoch_changed', 'session_changed'].includes(writer.error.message) ? 'web.sessionChanged' : writer.error instanceof Error && writer.error.message === 'offline' ? 'web.offline' : 'editor.couldNotSaveItemYourDraftIsKept')} action={writer.uncertain ? <Button onClick={() => void writer.checkStatus()}>{t('web.checkStatus')}</Button> : undefined}/>;
}
export function FormFrame({ title, children, onCancel, onSubmit, pending, error, submitLabel, disabled }: {
    title: string;
    children: ReactNode;
    onCancel: () => void;
    onSubmit: () => Promise<void> | void;
    pending?: boolean;
    error?: ReactNode;
    submitLabel?: string;
    disabled?: boolean;
}) {
    const { t } = useCopy();
    const [failed, setFailed] = useState(false);
    const mounted=useRef(true);useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
    function submit(event: FormEvent) { event.preventDefault(); setFailed(false); void Promise.resolve().then(()=>mounted.current?onSubmit():undefined).catch(() => {if(mounted.current)setFailed(true);}); }
    return <form className="spaces-form" onSubmit={submit}><header><h2>{title}</h2><Button variant="plain" onClick={onCancel}>{t('common.cancel')}</Button></header>{children}{error}{failed && <Banner title={t('ui.conflict')} tone="error"/>}<footer><Button variant="prominent" type="submit" loading={pending} disabled={disabled}>{submitLabel ?? t('common.save')}</Button></footer></form>;
}
export interface FieldSpec {
    key: string;
    label: string;
    type?: 'text' | 'textarea' | 'date' | 'datetime-local' | 'number';
    required?: boolean;
    maxLength?: number;
    options?: {
        value: string;
        label: string;
        disabled?: boolean;
    }[];
    initial?: string;
    hint?: string;
}
export function FieldsSheet({ title, fields, onCancel, onSave, submitLabel }: {
    title: string;
    fields: FieldSpec[];
    onCancel: () => void;
    onSave: (values: Record<string, string>) => Promise<void>;
    submitLabel?: string;
}) {
    const { t } = useCopy();
    const [values, setValues] = useState(() => Object.fromEntries(fields.map(f => [f.key, f.initial ?? ''])));
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(false);
    return <FormFrame title={title} onCancel={onCancel} pending={pending} submitLabel={submitLabel} onSubmit={async () => {
            setPending(true);
            setError(false);
            try {
                await onSave(values);
            }
            catch {
                setError(true);
            }
            finally {
                setPending(false);
            }
        }} error={error && <Banner tone="error" title={t('ui.conflict')}/>}>
    {fields.map(field => field.options ? <Select key={field.key} label={field.label} options={field.options} value={values[field.key]} required={field.required} onChange={e => setValues({ ...values, [field.key]: e.target.value })}/> : field.type === 'textarea' ? <TextArea key={field.key} label={field.label} value={values[field.key]} required={field.required} maxLength={field.maxLength} hint={field.hint} onChange={e => setValues({ ...values, [field.key]: e.target.value })}/> : <TextField key={field.key} label={field.label} type={field.type ?? 'text'} value={values[field.key]} required={field.required} maxLength={field.maxLength} hint={field.hint} onChange={e => setValues({ ...values, [field.key]: e.target.value })}/>)}
  </FormFrame>;
}
export function useFieldsSheet() {
    const nav = useNavigation();
    return (title: string, fields: FieldSpec[], onSave: (values: Record<string, string>) => Promise<void>, submitLabel?: string) => {
        const handle = nav.present(<FieldsSheet title={title} fields={fields} onCancel={() => handle.dismiss()} onSave={async (values) => { await onSave(values); handle.dismiss(); }} submitLabel={submitLabel}/>, { size: 'medium' });
        return handle;
    };
}
export function itemTitle(item: ItemRecord) { return `${item.number ? `#${item.number} ` : ''}${item.title}`; }
export function run(action: () => Promise<unknown>) { void action().catch(() => undefined); }
